import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import trackingController from '../services/trackingController';
import waypointQueue, { TERMINAL_UPLOAD_CODES, type QueueStatus } from '../services/waypointQueue';
import jobService from '../services/jobs';
import sprayService from '../services/spray';
import {
  DISCLOSURE_KEY,
  hasAcceptedLocationDisclosure,
  setLocationDisclosureAccepted,
} from '../services/locationGate';
import { describeCadence, type CadenceHints } from '../utils/gpsCadence';

export interface WaypointTrackingState {
  tracking: boolean;
  waypointCount: number;
  pendingCount: number;
  lastLocation: { lat: number; lng: number } | null;
  gpsStatus: 'good' | 'poor' | 'unavailable';
  currentSpeedKmh: number | null;
  recentSpeeds: (number | null)[];
  /** "Recording a point every 10 m and at each turn", while tracking. */
  cadenceLabel: string | null;
  error: string | null;
  /** Last observed upload error (or null). */
  uploadError: QueueStatus['lastError'];
  /** Consecutive failed upload batches since last success. */
  uploadFailures: number;
  showDisclosure: boolean;
  startTracking: () => void;
  stopTracking: () => void;
  /** Trigger an immediate upload attempt (for a manual "Retry" button). */
  retryUpload: () => void | Promise<void>;
  acceptDisclosure: () => void;
}

// Re-export for backwards-compat with any direct imports of this constant;
// new code should import from '../services/locationGate'.
export { DISCLOSURE_KEY };

/**
 * Thin React wrapper over `trackingController`.
 *
 * The hook no longer owns GPS state — it reads from the app-level singleton
 * via `useSyncExternalStore`, so navigating away from a page does NOT stop
 * tracking or tear down the foreground service. Only explicit stopTracking()
 * (or completing the job) shuts it down.
 */
export function useWaypointTracking(
  entityType: 'job' | 'spray',
  entityId: number,
  isActive: boolean,
  hints?: CadenceHints,
): WaypointTrackingState {
  const state = useSyncExternalStore(
    (cb) => trackingController.subscribe(cb),
    () => trackingController.getState(),
    () => trackingController.getState(),
  );

  const queueStatus = useSyncExternalStore(
    (cb) => waypointQueue.subscribeStatus(cb),
    () => waypointQueue.getStatus(),
    () => waypointQueue.getStatus(),
  );

  const [showDisclosure, setShowDisclosure] = useState(false);

  const isThisEntity =
    state.entity != null && state.entity.type === entityType && state.entity.id === entityId;
  const tracking = state.tracking && isThisEntity;

  const widthM = hints?.workingWidthM ?? null;
  const areaHa = hints?.targetAreaHa ?? null;
  const doStart = useCallback(() => {
    const entityHints = widthM != null || areaHa != null ? { workingWidthM: widthM, targetAreaHa: areaHa } : undefined;
    trackingController.start({ type: entityType, id: entityId, hints: entityHints }).catch((e) => {
      console.error('[useWaypointTracking] start failed', e);
    });
  }, [entityType, entityId, widthM, areaHa]);

  const startTracking = useCallback(() => {
    if (!hasAcceptedLocationDisclosure()) {
      setShowDisclosure(true);
      return;
    }
    doStart();
  }, [doStart]);

  const acceptDisclosure = useCallback(() => {
    setLocationDisclosureAccepted();
    setShowDisclosure(false);
    doStart();
  }, [doStart]);

  const stopTracking = useCallback(() => {
    void trackingController.stop();
  }, []);

  const retryUpload = useCallback(async () => {
    // If the queue is stuck on 409 ("X paused on the server"), the assignment
    // and job rows are out of sync — calling resume on the backend reconciles
    // both rows (the resume endpoints are idempotent post the self-healing
    // fix), then we drain the queue. Without this, an operator on the page
    // showing "Pause" but with a 409 banner has no UI path to recover, since
    // tapping Pause would only deepen the inconsistency.
    // ...but not when the entity is CLOSED. Resuming a finished job to flush
    // points the server has already refused for good would reopen it, and the
    // queue has discarded them anyway.
    if (
      queueStatus.lastError?.status === 409 &&
      !TERMINAL_UPLOAD_CODES.has(queueStatus.lastError.code ?? '')
    ) {
      try {
        if (entityType === 'job') await jobService.resumeJob(entityId);
        else await sprayService.resumeApplication(entityId);
      } catch (e) {
        console.warn('[useWaypointTracking] resume-on-retry failed', e);
      }
    }
    void waypointQueue.flush({ drain: true });
  }, [queueStatus.lastError?.status, queueStatus.lastError?.code, entityType, entityId]);

  // Auto-stop when the parent entity becomes inactive (e.g. job is completed
  // from another source). Does NOT stop on unmount — that's the whole point.
  useEffect(() => {
    if (!isActive && tracking) void trackingController.stop();
  }, [isActive, tracking]);

  return {
    tracking,
    // Show recorded + pending counts whenever this entity is the active one,
    // even if GPS capture is currently paused. Avoids the "pause wiped my
    // points" perception when the numbers are really just hidden.
    waypointCount: isThisEntity ? state.waypointCount : 0,
    pendingCount: isThisEntity ? state.pendingCount : 0,
    lastLocation: tracking ? state.lastLocation : null,
    gpsStatus: tracking ? state.gpsStatus : 'unavailable',
    currentSpeedKmh: tracking ? state.currentSpeedKmh : null,
    recentSpeeds: tracking ? state.recentSpeeds : [],
    cadenceLabel: tracking && state.cadence ? describeCadence(state.cadence) : null,
    error: state.error,
    // Only surface upload errors that belong to THIS page's entity. The queue
    // is shared across all entities, so without this filter a stuck old
    // record from a paused job would keep popping the 409 banner on a fresh
    // job's page even though the new job's uploads were succeeding fine.
    uploadError:
      queueStatus.lastError &&
      queueStatus.lastError.entityType === entityType &&
      queueStatus.lastError.entityId === entityId
        ? queueStatus.lastError
        : null,
    uploadFailures: queueStatus.consecutiveFailures,
    showDisclosure,
    startTracking,
    stopTracking,
    retryUpload,
    acceptDisclosure,
  };
}
